import { FormInitializerReturnType } from 'src/Configuration/validation'
import { WallboxConfigurationModel } from '../..'
import { CombinedChargeStrategyConfigurationForm, CommonModel } from '../../models/wallbox-configuration/wallbox-configuration-settings-form.model'
import { ChargeStrategyEnum, WallboxType } from '../../dto/types'
import { chargeStrategyConfigurationToForm, weekdayToForm } from '../../transformers/weekday-form.transformer'
import { object } from 'yup'
import { ChargeStrategyTypeEnum, CombinedChargeStrategyDto } from '../../types'

import { CHARGING_DAYS } from '@e3dc-react/shell/Libs/date/types'
import useWallboxSettingsFormValidationFields from '../use-wallbox-settings-form-field-validations'
const LED_INTENSITY_FALLBACK = 100
const CABLE_LOCK_FALLBACK = false
const initialChargeStrategy: CombinedChargeStrategyDto = {
  chargeStrategyType: ChargeStrategyTypeEnum.combined,
}

export const initialChargeStrategyConfiguration: CombinedChargeStrategyConfigurationForm[] = CHARGING_DAYS.map((_, index) => ({
  chargeStrategyWeekday: index,
  chargeStrategyUnit: 0,
  chargeStrategyDirectChargeAmount: 0,
  chargeStrategyDelayedChargeAmount: 0,
  chargeStrategyDelayedChargeTime: '00:00',
  chargeStrategyTotalChargeAmount: 0,
}))

/**
 * Initializes the form for editing wallbox common settings
 * @param model WallboxConfigurationModel
 * @returns initialValues and validationSchema for wallbox common settings form
 */
export const useWallboxCommonFormInitializer = (model?: WallboxConfigurationModel): FormInitializerReturnType<CommonModel, CommonModel> => {
  const validationFields = useWallboxSettingsFormValidationFields()
  const wallboxType = model?.wallboxType || WallboxType.DefaultE3dc

  const initialValues: CommonModel = {
    deviceName: model?.deviceName ?? '',
    ledIntensity: model?.ledIntensity ?? LED_INTENSITY_FALLBACK,
    autoPhaseSwitchEnabled: Boolean(model?.autoPhaseSwitchEnabled),
    cableLock: model?.cableLock ?? CABLE_LOCK_FALLBACK,
    wallboxType,
    /* used only in software version < X_2024_04 */
    activeChargeStrategy: model?.activeChargeStrategy ?? ChargeStrategyEnum.deactivated,
    weekdays: model?.weekdays?.map(weekdayToForm) ?? [],
    /* chargeStrategy, chargeStrategyConfiguration used only in software version >= X_2024_04 */
    chargeStrategy: model?.chargeStrategy || initialChargeStrategy,

    /* 0: wallbox limited by current (A); 1: wallbox limited by power (W); undefined: treat as 0; writable: no */
    chargeLimitType: model?.chargeLimitType ?? 0,
    chargeStrategyConfiguration:
      model?.chargeStrategyConfiguration?.map(configuration => chargeStrategyConfigurationToForm(configuration, wallboxType)) ??
      initialChargeStrategyConfiguration,
  }

  const validationSchema = object().shape({
    deviceName: validationFields.deviceNameField(),
    ledIntensity: validationFields.ledIntensityField(),
    autoPhaseSwitchEnabled: validationFields.autoPhaseSwitchEnabledField(),
    cableLock: validationFields.cableLockField(),
    activeChargeStrategy: validationFields.activeChargeStrategyField(),
    weekdays: validationFields.weekdaysField(),
    chargeStrategy: validationFields.chargeStrategyField(),
    chargeStrategyConfiguration: validationFields.chargeStrategyConfigurationField(),
    chargeLimitType: validationFields.chargeLimitTypeField(),
    wallboxType: validationFields.wallboxTypeField(),
  })

  return { initialValues, validationSchema }
}
